import { whiteCheckUrl } from '@common/utils/constants'
import {
  Checkbox,
  type CheckboxProps,
  FormControlLabel,
  type FormControlLabelProps,
  type SxProps,
  type Theme,
  useTheme
} from '@mui/material'
import { molsonStyled } from '@theme'
import type { FC } from 'react'
import { MolsonBox } from '../molson-box'
import { secondLabelDefaultSx } from '../molson-checkbox/MolsonCheckbox'
import {
  BaseIcon,
  CheckedIcon
} from '../molson-default-checkbox/MolsonDefaultCheckbox'
import { MolsonIcon } from '../molson-icons'
import { MolsonRadioButton } from '../molson-radio-button'
import { MolsonRadioGroup } from '../molson-radio-group'
import { MolsonTypography } from '../molson-typography'

export type MolsonArchivedProps = CheckboxProps & {
  dataTestId?: string
  checked?: boolean
  label?: string
  secondLabel?: string
  secondLabelSx?: SxProps<Theme>
  controlSx?: SxProps<Theme>
  outerBoxSx?: SxProps<Theme>
  hasRadioButton?: boolean
  radioChecked?: boolean
}

export type IFormControlLabelProps = FormControlLabelProps & {
  archived?: boolean
}

const CustomFormControlLabel: FC<IFormControlLabelProps> = props => (
  <FormControlLabel {...props} />
)

const StyledFormControlLabel = molsonStyled(CustomFormControlLabel)(
  ({ theme }: { theme: Theme }) => ({
    '&': {
      textDecoration: 'line-through',
      textDecorationThickness: '0.5px',
      color: theme.palette.primary.error,

      '& .MuiFormControlLabel-label': {
        color: theme.palette.primary.error,
        fontWeight: theme.typography.fontWeightMedium
      },

      '& .MuiFormControlLabel-label.Mui-disabled': {
        color: `${theme.palette.primary.error} !important`,
        cursor: 'not-allowed'
      }
    }
  })
)

const StyledRadioGroup = molsonStyled(MolsonRadioGroup)(
  ({ theme }: { theme: Theme }) => ({
    '&': {
      '& .MuiButtonBase-root.Mui-disabled': {
        color: theme.palette.primary.errorDisabled
      },
      '& .MuiFormControlLabel-label.Mui-disabled': {
        color: theme.palette.primary.error,
        textDecoration: 'line-through'
      }
    }
  })
)

const ArchivedCheckedIcon = molsonStyled(CheckedIcon)(({ theme }) => ({
  backgroundColor: theme.palette.primary.errorLight,
  border: `1px solid ${theme.palette.primary.errorLight}`,
  '&:before': {
    backgroundImage: whiteCheckUrl
  },
  'input:disabled ~ &': {
    border: `1px solid ${theme.palette.primary.errorLight}`
  }
}))

export const MolsonArchived: FC<MolsonArchivedProps> = ({
  dataTestId,
  label,
  secondLabel,
  outerBoxSx,
  secondLabelSx,
  controlSx,
  checked,
  hasRadioButton,
  radioChecked,
  ...props
}) => {
  const theme = useTheme()
  const { disabled } = props

  const borderColor = () => {
    const border = '1.5px solid '
    if (checked) {
      return border + theme.palette.primary.error
    }
    return ''
  }

  const backgroundColor = () => {
    if (checked) {
      return theme.palette.primary.errorLightest
    }
    return ''
  }

  const customerSecondLabelDefaultSx: SxProps<Theme> = {
    ...secondLabelDefaultSx(theme),
    color: theme.palette.primary.error,
    textDecoration: 'line-through',
    textDecorationThickness: '0.5px'
  }

  const defaultTypographySx: SxProps<Theme> = {
    color: theme.palette.primary.error,
    textDecoration: 'line-through',
    textDecorationThickness: '0.5px',
    fontWeight: 600,
    fontSize: 16,
    alignSelf: 'center'
  }

  return (
    <MolsonBox
      sx={{
        py: 2.25,
        px: 2,
        borderRadius: 2,
        border: borderColor(),
        backgroundColor: backgroundColor(),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'space-between',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          cursor: disabled ? 'not-allowed' : 'auto'
        },
        ...outerBoxSx
      }}
    >
      <MolsonBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        sx={controlSx}
      >
        {hasRadioButton && (
          <StyledRadioGroup value="" theme={theme}>
            <MolsonRadioButton
              formControlLabelProps={{
                label,
                sx: {
                  borderColor: 'transparent'
                }
              }}
              radioBtnProps={{
                disabled: true,
                value: '',
                checked: radioChecked
              }}
              checked={checked}
              error={true}
            />
          </StyledRadioGroup>
        )}

        {checked ? (
          <StyledFormControlLabel
            control={
              <Checkbox
                checked={checked}
                data-testid={dataTestId}
                {...props}
                checkedIcon={<ArchivedCheckedIcon />}
                icon={<BaseIcon />}
              />
            }
            label={label}
            theme={theme}
          />
        ) : (
          <MolsonTypography sx={defaultTypographySx}>{label}</MolsonTypography>
        )}

        {secondLabel && (
          <MolsonTypography
            sx={{ ...customerSecondLabelDefaultSx, ...secondLabelSx }}
          >
            {secondLabel}
          </MolsonTypography>
        )}
      </MolsonBox>

      <MolsonBox
        alignItems="center"
        display="flex"
        flexDirection="column"
        alignSelf="center"
        justifyContent="center"
      >
        <MolsonIcon variant="WARNING" iconColor="error" />
        <MolsonTypography color="error" fontSize={12}>
          Unavailable
        </MolsonTypography>
      </MolsonBox>
    </MolsonBox>
  )
}
